import { useState } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Trash2, Plus , MapPin } from "lucide-react";
import usePaginatedList from "@/hooks/usePaginatedList";
import Pagination from "@/components/panel/Pagination";
import { Button, Field, Input } from "@/components/ui-kit";
import { ConfirmDialog, EmptyState, ErrorState, TableSkeleton } from "@/components/panel/ui";

const EMPTY = { label: "Casa", cep: "", street: "", number: "", complement: "", district: "", city: "", state: "SP" };
const PER_PAGE = 10; // lista curta (teto de ~20): a paginação só aparece se passar disso

export default function Addresses() {
  // `retry` aqui é o refetch do hook — serve tanto ao ErrorState quanto ao
  // recarregamento após salvar/excluir um endereço.
  const {
    items: addresses, total, page, lastPage, setPage,
    isLoading: loading, isError: error, refetch: retry,
  } = usePaginatedList("/me/addresses", {}, { perPage: PER_PAGE });
  const [form, setForm] = useState(EMPTY);
  const [showForm, setShowForm] = useState(false);
  const [toDelete, setToDelete] = useState(null);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async () => {
    if (!form.cep || !form.street || !form.city) { toast.error("Preencha CEP, rua e cidade"); return; }
    try {
      await api.post("/me/addresses", form);
      toast.success("Endereço salvo"); setForm(EMPTY); setShowForm(false); retry();
    } catch (e) { toast.error(apiError(e)); }
  };
  const remove = async (a) => {
    if (!a) return;
    try {
      await api.delete(`/me/addresses/${a.id}`);
      toast.success("Endereço excluído"); retry();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-4" data-testid="addresses-page">
      <Button icon={Plus} onClick={() => setShowForm(!showForm)} data-testid="address-new-btn"
        aria-expanded={showForm}>Novo endereço</Button>
      {showForm && (
        /* Formulário do cliente: cada campo com rótulo visível. Antes eram sete
           placeholders soltos — e placeholder some quando a pessoa digita. */
        <div className="bg-white border border-slate-200 rounded-xl p-5 grid grid-cols-2 gap-3">
          <Field label="Apelido" labelTone="plain" hint="Casa, Trabalho...">
            <Input value={form.label} onChange={set("label")} data-testid="address-label-input" />
          </Field>
          <Field label="CEP" labelTone="plain">
            <Input value={form.cep} onChange={set("cep")} inputMode="numeric" autoComplete="postal-code" mono data-testid="address-cep-input" />
          </Field>
          <Field label="Rua" labelTone="plain" span>
            <Input value={form.street} onChange={set("street")} autoComplete="address-line1" data-testid="address-street-input" />
          </Field>
          <Field label="Número" labelTone="plain">
            <Input value={form.number} onChange={set("number")} inputMode="numeric" data-testid="address-number-input" />
          </Field>
          <Field label="Bairro" labelTone="plain">
            <Input value={form.district} onChange={set("district")} data-testid="address-district-input" />
          </Field>
          <Field label="Cidade" labelTone="plain">
            <Input value={form.city} onChange={set("city")} autoComplete="address-level2" data-testid="address-city-input" />
          </Field>
          <Field label="UF" labelTone="plain">
            <Input value={form.state} onChange={set("state")} maxLength={2} autoComplete="address-level1" className="uppercase" data-testid="address-state-input" />
          </Field>
          <Button onClick={save} size="lg" block className="col-span-2" data-testid="address-save-btn">Salvar endereço</Button>
        </div>
      )}
      {loading ? (
        <TableSkeleton rows={2} cols={2} />
      ) : error ? (
        <ErrorState onRetry={retry} message="Não foi possível carregar seus endereços." />
      ) : (
        <>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {addresses.map((a) => (
            <div key={a.id} className="bg-white border border-slate-200 rounded-xl p-4 flex justify-between" data-testid="address-card">
              <div className="text-sm">
                <p className="font-semibold text-slate-900">{a.label}</p>
                <p className="text-slate-600 mt-1">{a.street}, {a.number} {a.complement && `- ${a.complement}`}</p>
                <p className="text-slate-500">{a.district} · {a.city}/{a.state} · CEP {a.cep}</p>
              </div>
              <Button variant="ghost" size="iconSm" onClick={() => setToDelete(a)} data-testid="address-delete-btn"
                aria-label={`Excluir endereço ${a.label}`} className="hover:text-red-500 h-fit"><Trash2 size={15} /></Button>
            </div>
          ))}
          {addresses.length === 0 && !showForm && (
            <div className="md:col-span-2">
              <EmptyState icon={MapPin} title="Nenhum endereço salvo"
                desc="Cadastre um endereço para agilizar o checkout — ele fica disponível nas próximas compras."
                action={<Button icon={Plus} onClick={() => setShowForm(true)}>Cadastrar endereço</Button>}
                testId="addresses-empty" />
            </div>
          )}
        </div>
        <Pagination page={page} lastPage={lastPage} total={total} perPage={PER_PAGE} onChange={setPage} label="endereços" />
        </>
      )}

      <ConfirmDialog
        open={!!toDelete}
        onClose={() => setToDelete(null)}
        onConfirm={() => remove(toDelete)}
        title="Excluir endereço"
        message={toDelete ? `Excluir o endereço "${toDelete.label}"? Esta ação não pode ser desfeita.` : ""}
        confirmLabel="Excluir"
      />
    </div>
  );
}
